import { useState, useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import { InsetGroup, InsetRow } from '../../components/boothly/inset-list.jsx';
import { EmptyState, SkeletonStatTile } from '../../components/boothly/index.js';
import { fmtCurrency } from '../../lib/format.js';

const timeRanges = ['Diese Woche', 'Diesen Monat', 'Dieses Jahr', 'Gesamt'];
const SHORT = { 'Diese Woche': 'Woche', 'Diesen Monat': 'Monat', 'Dieses Jahr': 'Jahr', Gesamt: 'Gesamt' };

function getFilteredSessions(sessions, range) {
  const past = (sessions || []).filter(s => s.ends_at);
  if (range === 'Gesamt') return past;
  const now = new Date();
  return past.filter(s => {
    const d = new Date(s.starts_at || s.ends_at);
    if (range === 'Diese Woche') {
      const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
      return d >= weekAgo;
    }
    if (range === 'Diesen Monat') {
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }
    if (range === 'Dieses Jahr') {
      return d.getFullYear() === now.getFullYear();
    }
    return true;
  });
}

export function Statistik({ sessions, dark, loading }) {
  const [activeRange, setActiveRange] = useState('Diesen Monat');

  const stats = useMemo(() => {
    const filtered = getFilteredSessions(sessions, activeRange);
    const allSales = filtered.flatMap(s => (s.sales || [])).filter(s => !s.is_voided);
    const totalRevenue = allSales.reduce((sum, s) => sum + (s.total || 0), 0);
    const totalSales = allSales.length;
    const avgPrice = totalSales > 0 ? totalRevenue / totalSales : 0;

    // Gewinn nach Standgebühr = Umsatz − Summe der booth_fee der Sessions im Zeitraum.
    // Nur Sessions mit gesetzter Gebühr zählen (null = nicht angegeben).
    const totalBoothFee = filtered.reduce((sum, s) => sum + (s.booth_fee != null ? s.booth_fee : 0), 0);
    const hasBoothFee = filtered.some(s => s.booth_fee != null);

    // Wish-price ratio — liest sale_items (NICHT s.items, das existiert nicht).
    // Greift wo sale_items eingebettet sind (Seed/Detail); sonst bleibt die Quote '—'.
    const withWish = allSales.filter(s => s.sale_items?.some(i => i.wish_price > 0));
    let priceRatio = 0;
    if (withWish.length > 0) {
      const totalActual = withWish.reduce((sum, s) => sum + (s.total || 0), 0);
      const totalWish = withWish.reduce((sum, s) => sum + (s.sale_items || []).reduce((is, i) => is + (i.wish_price || 0), 0), 0);
      priceRatio = totalWish > 0 ? Math.round((totalActual / totalWish) * 100) : 0;
    }

    // Best market day
    let bestMarket = { name: '\u2014', revenue: 0 };
    for (const s of filtered) {
      const rev = (s.sales || []).filter(sl => !sl.is_voided).reduce((sum, sl) => sum + (sl.total || 0), 0);
      // Also check stats from include_stats=1
      const sessionRev = s.revenue ?? s.total_revenue ?? rev;
      if (sessionRev > bestMarket.revenue) {
        bestMarket = { name: s.location || s.name || new Date(s.starts_at).toLocaleDateString('de-DE'), revenue: sessionRev };
      }
    }

    return { totalRevenue, totalSales, avgPrice, marketDays: filtered.length, priceRatio, bestMarket, totalBoothFee, hasBoothFee };
  }, [sessions, activeRange]);

  const activeIdx = Math.max(0, timeRanges.indexOf(activeRange));

  return (
    <div data-testid="screen-troedel-statistik" className="space-y-6 pt-2" style={{ paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Zeitraum — iOS Segmented Control (44pt) */}
      <div role="tablist" aria-label="Zeitraum" className="relative flex h-11 rounded-[12px] bg-[var(--fill-tertiary)] p-[3px]">
        <span
          aria-hidden="true"
          className="absolute bottom-[3px] top-[3px] rounded-[9px] bg-[var(--segment-selected)] shadow-[var(--shadow-sm)]"
          style={{
            left: `calc(3px + ${activeIdx} * (100% - 6px) / ${timeRanges.length})`,
            width: `calc((100% - 6px) / ${timeRanges.length})`,
            transition: 'left 250ms var(--ease-out-ios)',
          }}
        />
        {timeRanges.map(range => (
          <button
            key={range}
            role="tab"
            aria-selected={activeRange === range}
            aria-label={range}
            data-testid={
              range === 'Diese Woche' ? 'zeitraum-tab-woche'
              : range === 'Diesen Monat' ? 'zeitraum-tab-monat'
              : range === 'Dieses Jahr' ? 'zeitraum-tab-jahr'
              : undefined
            }
            onClick={() => setActiveRange(range)}
            className={`relative z-[1] flex-1 text-[15px] ${activeRange === range ? 'font-semibold' : 'text-[var(--label-secondary)]'}`}
          >
            {SHORT[range]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonStatTile key={i} />
          ))}
        </div>
      ) : stats.marketDays > 0 && (
        <>
          <div data-testid="stat-tile-troedel-umsatz" className="px-1">
            <p className="text-[13px] text-[var(--label-secondary)]">Umsatz</p>
            <p className="num text-[40px] font-bold leading-[1.05] tracking-[-0.02em]">{fmtCurrency(stats.totalRevenue)}</p>
            <p className="mt-1 text-[15px] text-[var(--label-secondary)]">
              {`${stats.totalSales} Verkäufe an ${stats.marketDays} ${stats.marketDays === 1 ? 'Markttag' : 'Markttagen'}`}
            </p>
          </div>

          {/* Gewinn nach Standgebühr — nur wenn im Zeitraum mind. eine Gebühr hinterlegt ist */}
          {stats.hasBoothFee && (
            <InsetGroup>
              <InsetRow
                first
                testId="stat-tile-troedel-gewinn"
                title="Gewinn nach Standgebühr"
                subtitle={<span className="num">{`Umsatz ${fmtCurrency(stats.totalRevenue)} − Gebühren ${fmtCurrency(stats.totalBoothFee)}`}</span>}
                value={
                  <span className="whitespace-nowrap font-bold" style={{ color: stats.totalRevenue - stats.totalBoothFee >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                    {fmtCurrency(stats.totalRevenue - stats.totalBoothFee)}
                  </span>
                }
                minHeight={60}
              />
            </InsetGroup>
          )}

          <InsetGroup title="Details">
            <InsetRow first testId="stat-tile-troedel-verkaeufe" title="Verkäufe" value={stats.totalSales} valueClassName="font-semibold" />
            <InsetRow
              testId="stat-tile-troedel-wunschpreis"
              title="Vom Wunschpreis erzielt"
              value={stats.priceRatio > 0 ? `${stats.priceRatio}\u202F%` : '\u2014'}
              valueClassName="font-semibold"
            />
            <InsetRow
              testId="stat-tile-troedel-bester-tag"
              title="Bester Markttag"
              subtitle={<span className="block truncate">{stats.bestMarket.name}</span>}
              value={fmtCurrency(stats.bestMarket.revenue)}
              valueClassName="font-semibold"
              minHeight={60}
            />
          </InsetGroup>
        </>
      )}

      {/* Empty state if no sessions in selected range */}
      {!loading && stats.marketDays === 0 && (
        <EmptyState
          icon={BarChart3}
          title="Noch keine Daten"
          description="Sobald du Verkäufe registrierst, siehst du hier deine Statistiken."
          cta={activeRange !== 'Gesamt'
            ? { label: 'Zeitraum: Gesamt anzeigen', onClick: () => setActiveRange('Gesamt'), accent: 'troedel' }
            : undefined}
        />
      )}
    </div>
  );
}
